import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';

import Login from '@/pages/Login';
import Landing from '@/pages/Landing';
import Layout from '@/components/Layout';
import SensitiveContentGuard from '@/components/SensitiveContentGuard';
//import OutstandingAnalysis from './pages/OutstandingAnalysis';

import offlineSyncService from '@/lib/offlineSync';
import { SettingsProvider } from '@/context/SettingsContext';
import useKeyboardNavigation from '@/hooks/useKeyboardNavigation';
import '@/App.css';

/* Lazy Loaded Pages */
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const ItemMaster = lazy(() => import('@/pages/ItemMaster'));
const Categories = lazy(() => import('@/pages/Categories'));
const ItemTypes = lazy(() => import('@/pages/ItemTypes'));
const LiveStock = lazy(() => import('@/pages/LiveStock'));
const ProductCatalog = lazy(() => import('@/pages/ProductCatalog'));
const RecordSale = lazy(() => import('@/pages/RecordSale'));
const RecordSalesReturn = lazy(() => import('@/pages/RecordSalesReturn'));
const RecordPurchase = lazy(() => import('@/pages/RecordPurchase'));
const RecordPurchaseReturn = lazy(() => import('@/pages/RecordPurchaseReturn'));
const Sales = lazy(() => import('@/pages/Sales'));
const Purchases = lazy(() => import('@/pages/Purchases'));
const SalesReturn = lazy(() => import('@/pages/SalesReturn'));
const Customers = lazy(() => import('@/pages/Customers'));
const Sellers = lazy(() => import('@/pages/Sellers'));
const TransferStock = lazy(() => import('@/pages/TransferStock'));
const StockTransferHistory = lazy(() => import('@/pages/StockTransferHistory'));
const OutletStock = lazy(() => import('@/pages/OutletStock'));
const Accounts = lazy(() => import('@/pages/Accounts'));
const StaffManagement = lazy(() => import('@/pages/Users'));
const Roles = lazy(() => import('@/pages/Roles'));
const Logs = lazy(() => import('@/pages/Logs'));
const StockLedger = lazy(() => import('@/pages/StockLedger'));
const Backup = lazy(() => import('@/pages/Backup'));
const StockAdjustment = lazy(() => import('@/pages/StockAdjustment'));
const StockAudit = lazy(() => import('@/pages/StockAudit'));
const StockAuditScanner = lazy(() => import('@/pages/StockAuditScanner'));
const StockAuditReport = lazy(() => import('@/pages/StockAuditReport'));
const Attendance = lazy(() => import('@/pages/Attendance'));
const Payroll = lazy(() => import('@/pages/Payroll'));
const Settings = lazy(() => import('@/pages/Settings'));
const PurchaseRequest = lazy(() => import('@/pages/PurchaseRequest'));
const PurchaseOrder = lazy(() => import('@/pages/PurchaseOrder'));
const PurchaseReturn = lazy(() => import('@/pages/PurchaseReturn'));
const Vouchers = lazy(() => import('@/pages/Vouchers'));
const Ledgers = lazy(() => import('@/pages/Ledgers'));
const Reports = lazy(() => import('@/pages/Reports'));
const Outlets = lazy(() => import('@/pages/Outlets'));
const PublicInvoice = lazy(() => import('@/pages/public/PublicInvoice'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

//console.log("BACKEND URL:", BACKEND_URL); remove this line before pushing to production
const API = `${BACKEND_URL}/api`;

const LoginGuard = ({ children }) => {
  const loginAccessAt = localStorage.getItem('login_access_at');
  const now = Date.now();
  const isValid = loginAccessAt && (now - parseInt(loginAccessAt) < 120000);

  if (!isValid) return <Navigate to="/" replace />;
  return children;
};

const PermissionGuard = ({ children, module, action = 'view', user }) => {
  if (!user) return <Navigate to="/login" replace />;
  if (module === 'salesman_stock') return children;
  if (user.role_name === 'Admin') return children;

  const perms = user.permissions?.[module];
  if (!perms || !perms[action]) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const NavigationWrapper = ({ children }) => {
  useKeyboardNavigation();
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }

    setLoading(false);

    offlineSyncService.init().then(() => {
      if (navigator.onLine && token) {
        offlineSyncService.sync();
      }
    }).catch(err => {
      console.error('[App] Failed to initialize offline sync:', err);
    });
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (
        document.activeElement &&
        document.activeElement.type === 'number' &&
        e.target === document.activeElement
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setTimeout(() => {
      offlineSyncService.sync();
    }, 1000);
  };

  const handleLogout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const loginLogId = localStorage.getItem('login_log_id');

      if (token && loginLogId) {
        await axios.post(`${API}/auth/logout`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { login_log_id: loginLogId }
        });
      }
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('login_log_id');
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="text-2xl font-serif text-[#022c22]">Loading...</div>
      </div>
    );
  }

  return (
    <SettingsProvider>
      <BrowserRouter>
        <NavigationWrapper>
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
                <div className="text-2xl font-serif text-[#022c22]">Loading...</div>
              </div>
            }
          >
            <Routes>
              {!user ? (
                <>
                  <Route path="/" element={<Landing />} />
                  <Route
                    path="/login"
                    element={
                      <LoginGuard>
                        <Login onLogin={handleLogin} />
                      </LoginGuard>
                    }
                  />
                  <Route path="/public/invoice/:id" element={<PublicInvoice />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </>
              ) : (
                <>
                  <Route path="/public/invoice/:id" element={<PublicInvoice />} />
                  <Route element={<Layout user={user} onLogout={handleLogout} />}>
                    <Route path="/dashboard" element={<PermissionGuard module="dashboard" user={user}><SensitiveContentGuard user={user}><Dashboard /></SensitiveContentGuard></PermissionGuard>} />
                    <Route path="/sellers" element={<PermissionGuard module="sellers" user={user}><Sellers /></PermissionGuard>} />
                    <Route path="/salesman/issue-new" element={<PermissionGuard module="salesman_stock" action="create" user={user}><TransferStock /></PermissionGuard>} />
                    <Route path="/salesman/issues" element={<PermissionGuard module="salesman_stock" user={user}><StockTransferHistory /></PermissionGuard>} />
                    <Route path="/stock-transfer/stock/:outletId" element={<PermissionGuard module="salesman_stock" user={user}><OutletStock /></PermissionGuard>} />
                    <Route path="/live-stock" element={<PermissionGuard module="inventory" user={user}><LiveStock /></PermissionGuard>} />

                    <Route path="/product-catalog" element={<PermissionGuard module="inventory" user={user}><ProductCatalog /></PermissionGuard>} />
                    <Route path="/inventory" element={<PermissionGuard module="inventory" user={user}><Inventory /></PermissionGuard>} />
                    <Route path="/item-master" element={<PermissionGuard module="item_master" user={user}><ItemMaster /></PermissionGuard>} />
                    <Route path="/stock-ledger" element={<PermissionGuard module="stock_ledger" user={user}><StockLedger /></PermissionGuard>} />
                    <Route path="/stock-adjustment" element={<PermissionGuard module="stock_adjustment" user={user}><StockAdjustment /></PermissionGuard>} />
                    <Route path="/stock-audit" element={<StockAudit />} />
                    <Route path="/stock-audit/:auditId/scan" element={<StockAuditScanner />} />
                    <Route path="/stock-audit/:auditId/report" element={<StockAuditReport />} />

                    <Route path="/categories" element={<PermissionGuard module="categories" user={user}><Categories /></PermissionGuard>} />
                    <Route path="/item-types" element={<PermissionGuard module="item_types" user={user}><ItemTypes /></PermissionGuard>} />
                    <Route path="/sales/new" element={<PermissionGuard module="sales" action="create" user={user}><RecordSale /></PermissionGuard>} />
                    <Route path="/sales/edit/:id" element={<PermissionGuard module="sales" action="edit" user={user}><RecordSale /></PermissionGuard>} />
                    <Route path="/purchase/new" element={<PermissionGuard module="purchasing" action="create" user={user}><RecordPurchase /></PermissionGuard>} />
                    <Route path="/purchase/edit/:id" element={<PermissionGuard module="purchasing" action="edit" user={user}><RecordPurchase /></PermissionGuard>} />
                    <Route path="/sales" element={<PermissionGuard module="sales" user={user}><Sales /></PermissionGuard>} />
                    <Route path="/purchases" element={<PermissionGuard module="purchasing" user={user}><Purchases /></PermissionGuard>} />
                    <Route path="/sales-return" element={<PermissionGuard module="sales" action="edit" user={user}><SalesReturn /></PermissionGuard>} />
                    <Route path="/sales-return/new" element={<PermissionGuard module="sales" action="create" user={user}><RecordSalesReturn /></PermissionGuard>} />
                    <Route path="/sales-return/edit/:id" element={<PermissionGuard module="sales" action="edit" user={user}><RecordSalesReturn /></PermissionGuard>} />
                    <Route path="/customers" element={<PermissionGuard module="customers" user={user}><Customers /></PermissionGuard>} />
                    <Route path="/accounts" element={<PermissionGuard module="accounts" user={user}><Accounts /></PermissionGuard>} />
                    <Route path="/vouchers" element={<PermissionGuard module="vouchers" user={user}><Vouchers /></PermissionGuard>} />
                    <Route path="/ledgers" element={<PermissionGuard module="ledgers" user={user}><Ledgers /></PermissionGuard>} />
                    <Route path="/reports" element={<PermissionGuard module="reports" user={user}><SensitiveContentGuard user={user}><Reports /></SensitiveContentGuard></PermissionGuard>} />
                    <Route path="/users" element={<PermissionGuard module="users" user={user}><StaffManagement /></PermissionGuard>} />
                    <Route path="/roles" element={<PermissionGuard module="roles" user={user}><Roles /></PermissionGuard>} />
                    <Route path="/logs" element={<PermissionGuard module="logs" user={user}><Logs /></PermissionGuard>} />
                    <Route path="/attendance" element={<PermissionGuard module="attendance" user={user}><Attendance /></PermissionGuard>} />
                    <Route path="/outlets" element={<PermissionGuard module="outlets" user={user}><Outlets /></PermissionGuard>} />
                    <Route path="/payroll" element={<PermissionGuard module="payroll" user={user}><Payroll /></PermissionGuard>} />
                    <Route path="/settings" element={<PermissionGuard module="settings" user={user}><Settings /></PermissionGuard>} />
                    <Route path="/backup" element={<PermissionGuard module="backup" user={user}><Backup /></PermissionGuard>} />
                    <Route path="/purchase-request" element={<PermissionGuard module="purchasing" user={user}><PurchaseRequest /></PermissionGuard>} />
                    <Route path="/purchase-order" element={<PermissionGuard module="purchasing" user={user}><PurchaseOrder /></PermissionGuard>} />
                    <Route path="/purchase-return" element={<PermissionGuard module="purchasing" user={user}><PurchaseReturn /></PermissionGuard>} />
                    <Route path="/purchase-return/new" element={<PermissionGuard module="purchasing" action="create" user={user}><RecordPurchaseReturn /></PermissionGuard>} />
                    <Route path="/purchase-return/edit/:id" element={<PermissionGuard module="purchasing" action="edit" user={user}><RecordPurchaseReturn /></PermissionGuard>} />
                    <Route path="/" element={<Landing />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </>
              )}
            </Routes>
          </Suspense>
        </NavigationWrapper>
      </BrowserRouter>

      <Toaster position="top-right" />
    </SettingsProvider>
  );
}

export default App;
